/**
 * GatewayApiKeysPageV2 — proper v2 redesign (G115).
 *
 * Canonical inventory pattern.
 * Reuses every backend code path from v1 GatewayApiKeysPage:
 *   - api.gatewayListKeys / GetKey / CreateKey / RotateKey / RevokeKey /
 *     SuspendKey / ReactivateKey / UpdateKeyScope / GetKeyAudit /
 *     GetTenantAudit
 *
 * v1 stays as fallback via /gateway-keys?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAdministerEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Plus, Key, RotateCcw, XCircle, Pause, Play, Eye, Copy, AlertTriangle, Check, X,
} from 'lucide-react';
import {
  Button, Pill, Card, Slideout, Table, FacetRail, EmptyState,
  MetricCard,
} from '../components/v2';
import { GatewayScopePicker } from '../components/v2/GatewayScopePicker';
import { GatewayAccessTester } from '../components/v2/GatewayAccessTester';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import GatewayApiKeysPage from './GatewayApiKeysPage';

const STATUS_OPTIONS = [
  { key: 'ACTIVE',     label: 'Active',     tone: 'success' },
  { key: 'EXPIRING',   label: 'Expiring',   tone: 'warning' },
  { key: 'SUSPENDED',  label: 'Suspended',  tone: 'warning' },
  { key: 'REVOKED',    label: 'Revoked',    tone: 'danger' },
  { key: 'EXPIRED',    label: 'Expired',    tone: 'muted' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));

const SCOPE_OPTIONS = [
  { key: 'tenant',   label: 'Tenant-wide' },
  { key: 'agent',    label: 'Agent-scoped' },
  { key: 'workflow', label: 'Workflow-scoped' },
];

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = d.getTime() - Date.now();
  const days = Math.round(diff / 86400000);
  if (Math.abs(days) === 0) return 'today';
  if (days > 0) return `in ${days}d`;
  return `${-days}d ago`;
}

function scopeKind(k) {
  if (k.scopedAgentIds?.length > 0) return 'agent';
  if (k.scopedWorkflowIds?.length > 0) return 'workflow';
  return 'tenant';
}

function GatewayApiKeysPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);   // Slice G3 — gateway usage rollup (best-effort)

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.gatewayListKeys?.();
      const items = Array.isArray(res?.data?.items) ? res.data.items
                  : Array.isArray(res?.items) ? res.items
                  : Array.isArray(res?.data) ? res.data
                  : Array.isArray(res) ? res : [];
      setKeys(items);
      // Usage rollup is purely observational — its failure must not break the keys page.
      try {
        const dash = await api.gatewayGetDashboard?.();
        setUsage(dash?.data ?? dash ?? null);
      } catch { setUsage(null); }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({
    status: new Set(),
    scope:  new Set(),
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({ status: new Set(), scope: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status', label: 'Status', options: STATUS_OPTIONS.map(s => ({ key: s.key, label: s.label })) },
    { key: 'scope',  label: 'Scope',  options: SCOPE_OPTIONS },
  ]), []);

  const filtered = useMemo(() => {
    return keys.filter(k => {
      if (facets.status.size > 0 && !facets.status.has(k.status)) return false;
      if (facets.scope.size > 0 && !facets.scope.has(scopeKind(k))) return false;
      return true;
    });
  }, [keys, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = keys.filter(k => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (og.key === 'scope') { if (!sel.has(scopeKind(k))) return false; }
            else if (!sel.has(k[og.key])) return false;
          }
          if (g.key === 'scope') return scopeKind(k) === opt.key;
          return k[g.key] === opt.key;
        }).length;
      }
    }
    return counts;
  }, [keys, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('gateway-keys-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('gateway-keys-density', next); } catch {}
  };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const [confirmRevokeId, setConfirmRevokeId] = useState(null); // inline revoke confirm (no window.confirm)
  // Edit surface for the key's per-direction DPG actions (mirrors the Create form).
  const [dpgForm, setDpgForm] = useState({ prompt: '', completion: '', toolArgs: '', toolResult: '' });
  const [dpgSaving, setDpgSaving] = useState(false);
  const openSlideout = async (row) => {
    setSelected(row);
    setSlideoutTab('overview');
    try {
      const fresh = await api.gatewayGetKey?.(row.id);
      if (fresh?.ok) setSelected({ ...row, ...(fresh.data || {}) });
    } catch (err) { /* ignore */ }
  };
  const closeSlideout = () => setSelected(null);

  // Seed the DPG edit form from the selected key whenever it changes.
  useEffect(() => {
    setDpgForm({
      prompt: selected?.dpgPromptAction || '',
      completion: selected?.dpgCompletionAction || '',
      toolArgs: selected?.dpgToolArgsAction || '',
      toolResult: selected?.dpgToolResultAction || '',
    });
  }, [selected?.id, selected?.dpgPromptAction, selected?.dpgCompletionAction,
      selected?.dpgToolArgsAction, selected?.dpgToolResultAction]);

  const handleSaveDpg = async () => {
    if (!selected) return;
    setDpgSaving(true);
    try {
      const res = await api.gatewayUpdateKeyDpg?.(selected.id, {
        dpgPromptAction: dpgForm.prompt || null,
        dpgCompletionAction: dpgForm.completion || null,
        dpgToolArgsAction: dpgForm.toolArgs || null,
        dpgToolResultAction: dpgForm.toolResult || null,
      });
      if (res?.ok) {
        setSelected((s) => ({ ...s, ...(res.data || {}) }));
        reload();
        addToast({ tone: 'success', title: 'Data protection updated' });
      } else {
        addToast({ tone: 'danger', title: 'Update failed', description: res?.error || res?.data?.error });
      }
    } catch (err) {
      addToast({ tone: 'danger', title: 'Update failed', description: err?.message });
    }
    setDpgSaving(false);
  };

  /* G202 — V2-native Create API Key modal. Calls api.gatewayCreateKey.
   * Mirrors the v1 payload contract (name/description/keyType/expiresInDays/
   * rateLimitRpm/rateLimitBurst/permissions/ipAllowlist/scopedAgentIds/
   * scopedWorkflowIds). On success reveals the plaintext key exactly once
   * with copy-to-clipboard — the operator must save it before dismissing
   * since it is not stored server-side. */
  const CREATE_DEFAULTS = {
    name: '', description: '', keyType: 'GATEWAY', expiresInDays: 90,
    rateLimit: 60, burstLimit: 10, ipAllowlist: '',
    scopedAgentIds: '', scopedWorkflowIds: '',
    permissions: 'EXECUTE,READ',
    // Per-direction data-protection actions (raise-only vs the agent policy). '' = inherit.
    dpgPromptAction: '', dpgCompletionAction: '', dpgToolArgsAction: '', dpgToolResultAction: '',
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(CREATE_DEFAULTS);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [plaintextKey, setPlaintextKey] = useState(null);
  const [plaintextKeyId, setPlaintextKeyId] = useState(null);
  const [copyState, setCopyState] = useState('idle');
  // RC-155j — EXTERNAL keys mint a per-key HMAC shared secret alongside the
  // API key token. The BE returns both plaintextKey + plaintextHmacSecret in
  // the create response envelope (one-time-shown). Both must be captured +
  // displayed with individual copy buttons; without either, the customer's
  // external app cannot authenticate against /api/v1/external/workflows/**.
  const [plaintextHmacSecret, setPlaintextHmacSecret] = useState(null);
  const [hmacCopyState, setHmacCopyState] = useState('idle');

  const openCreate = () => {
    setCreateForm(CREATE_DEFAULTS);
    setCreateError(null);
    setPlaintextKey(null);
    setPlaintextKeyId(null);
    setPlaintextHmacSecret(null);
    setCopyState('idle');
    setHmacCopyState('idle');
    setCreateOpen(true);
  };
  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
  };
  const submitCreate = async () => {
    setCreateError(null);
    if (!createForm.name.trim()) { setCreateError('Name is required.'); return; }
    setCreating(true);
    try {
      const agentIds = createForm.scopedAgentIds
        .split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      const workflowIds = createForm.scopedWorkflowIds
        .split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      const ipAllowlist = createForm.ipAllowlist
        .split(/\n+/).map(s => s.trim()).filter(Boolean);
      // Backend DTO field is expiresAt (LocalDateTime), NOT expiresInDays. Sending
      // the unknown 'expiresInDays' field failed JSON deserialization ("Request
      // body could not be parsed"). Compute an ISO-LOCAL timestamp (no trailing Z —
      // LocalDateTime rejects a zone offset) from the days value.
      const _days = parseInt(createForm.expiresInDays, 10) || 90;
      const _expiresAt = new Date(Date.now() + _days * 24 * 60 * 60 * 1000)
        .toISOString().slice(0, 19);
      const payload = {
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        keyType: createForm.keyType,
        expiresAt: _expiresAt,
        rateLimitRpm: parseInt(createForm.rateLimit, 10) || 60,
        rateLimitBurst: parseInt(createForm.burstLimit, 10) || 10,
        // Backend expects List<String>; the form holds a comma/space string
        // ("EXECUTE,READ"). Sending the raw string failed JSON deserialization
        // ("Request body could not be parsed"). Split into an array.
        permissions: (Array.isArray(createForm.permissions)
          ? createForm.permissions
          : String(createForm.permissions || '').split(/[\s,]+/).map(s => s.trim()).filter(Boolean)),
      };
      if (ipAllowlist.length > 0) payload.ipAllowlist = ipAllowlist;
      // Per-key data-protection floor — only send when the operator picked one; blank = no floor.
      if (createForm.dpgPromptAction) payload.dpgPromptAction = createForm.dpgPromptAction;
      if (createForm.dpgCompletionAction) payload.dpgCompletionAction = createForm.dpgCompletionAction;
      if (createForm.dpgToolArgsAction) payload.dpgToolArgsAction = createForm.dpgToolArgsAction;
      if (createForm.dpgToolResultAction) payload.dpgToolResultAction = createForm.dpgToolResultAction;
      if (agentIds.length > 0 && createForm.keyType !== 'WORKER' && createForm.keyType !== 'EXTERNAL') {
        payload.scopedAgentIds = agentIds;
      }
      // RC-155j — WORKER and EXTERNAL keys are workflow-scoped, not agent-scoped.
      // The BE enforces that EXTERNAL keys carry a non-empty scopedWorkflowIds
      // (least-privilege — a tenant-wide customer-facing credential would be
      // too broad). Send the list for both key types.
      if (workflowIds.length > 0
          && (createForm.keyType === 'WORKER' || createForm.keyType === 'EXTERNAL')) {
        payload.scopedWorkflowIds = workflowIds;
      }

      const res = await api.gatewayCreateKey(payload);
      if (!res || res.ok === false) {
        setCreateError(res?.error || res?.data?.error || `Create failed (HTTP ${res?.status || '?'}).`);
        return;
      }
      const data = res.data || res;
      const pt = data?.plaintextKey || data?.key?.plaintextKey || null;
      const kid = data?.key?.id || data?.id || null;
      // RC-155j — EXTERNAL keys additionally carry a plaintextHmacSecret in
      // the response. Non-EXTERNAL keys leave it null; nothing to display in
      // that case.
      const hmac = data?.plaintextHmacSecret || data?.key?.plaintextHmacSecret || null;
      if (pt) {
        setPlaintextKey(pt);
        setPlaintextKeyId(kid);
        if (hmac) setPlaintextHmacSecret(hmac);
        addToast({ tone: 'success', title: 'API key created',
          description: hmac
            ? 'Copy the API key and signing secret now — neither can be retrieved again.'
            : 'Copy the plaintext key now — it cannot be retrieved again.' });
      } else {
        addToast({ tone: 'success', title: 'API key created' });
        setCreateOpen(false);
      }
      reload();
    } catch (e) {
      setCreateError(e?.message || String(e));
    } finally {
      setCreating(false);
    }
  };
  const copyPlaintext = async () => {
    if (!plaintextKey) return;
    try {
      await navigator.clipboard.writeText(plaintextKey);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      setCopyState('failed');
    }
  };
  // RC-155j — separate copy affordance for the HMAC shared secret. Both live
  // in the reveal modal side-by-side; operators need to copy each into their
  // secrets manager. Using a separate state variable so the two "Copied!"
  // labels don't clobber each other when clicked in quick succession.
  const copyHmacSecret = async () => {
    if (!plaintextHmacSecret) return;
    try {
      await navigator.clipboard.writeText(plaintextHmacSecret);
      setHmacCopyState('copied');
      setTimeout(() => setHmacCopyState('idle'), 2000);
    } catch (err) {
      setHmacCopyState('failed');
    }
  };

  const handleRotate = async (id) => {
    try {
      const res = await api.gatewayRotateKey?.(id, 24);
      if (!res || res.ok === false) {
        addToast({ tone: 'danger', title: 'Rotate failed',
          description: res?.error || res?.data?.error || `HTTP ${res?.status || '?'}` });
        return;
      }
      // Rotation mints a NEW key whose plaintext is returned exactly once
      // ({newKey, plaintextKey}). Previously this handler discarded it, so the
      // rotated key was unrecoverable. Reveal it through the same modal as create.
      const data = res.data || res;
      const pt = data?.plaintextKey || data?.newKey?.plaintextKey || data?.key?.plaintextKey || null;
      const kid = data?.newKey?.id || data?.key?.id || data?.id || null;
      if (pt) {
        setPlaintextKey(pt);
        setPlaintextKeyId(kid);
        setCreateOpen(true);   // reuse the reveal modal (gated on plaintextKey)
        addToast({ tone: 'success', title: 'Key rotated',
          description: 'Copy the NEW key now — it cannot be retrieved again. Old key valid 24h.' });
      } else {
        addToast({ tone: 'warning', title: 'Key rotated, but no secret returned',
          description: 'The server did not return the new plaintext — rotate again or create a fresh key.' });
      }
      reload();
    } catch (err) { addToast({ tone: 'danger', title: 'Rotate failed', description: err?.message }); }
  };
  // Inline two-step revoke confirm — platform-native, no browser window.confirm.
  // Clicking Revoke arms an inline Revoke / Cancel pair; auto-disarms after 5s.
  const armRevoke = (id) => {
    setConfirmRevokeId(id);
    setTimeout(() => setConfirmRevokeId((cur) => (cur === id ? null : cur)), 5000);
  };
  const commitRevoke = async (id) => {
    setConfirmRevokeId(null);
    try {
      const res = await api.gatewayRevokeKey?.(id, 'manual revoke');
      if (res?.ok) { addToast({ tone: 'info', title: 'Key revoked' }); closeSlideout(); reload(); }
    } catch (err) { addToast({ tone: 'danger', title: 'Revoke failed', description: err?.message }); }
  };
  const handleSuspend = async (id) => {
    try {
      const res = await api.gatewaySuspendKey?.(id, 'manual suspend');
      if (res?.ok) { addToast({ tone: 'info', title: 'Key suspended' }); reload(); }
    } catch (err) { addToast({ tone: 'danger', title: 'Suspend failed', description: err?.message }); }
  };
  const handleReactivate = async (id) => {
    try {
      const res = await api.gatewayReactivateKey?.(id);
      if (res?.ok) { addToast({ tone: 'success', title: 'Key reactivated' }); reload(); }
    } catch (err) { addToast({ tone: 'danger', title: 'Reactivate failed', description: err?.message }); }
  };

  const kpis = useMemo(() => {
    const active = keys.filter(k => k.status === 'ACTIVE').length;
    const expiring = keys.filter(k => {
      if (k.status !== 'ACTIVE' || !k.expiresAt) return false;
      const days = Math.round((new Date(k.expiresAt).getTime() - Date.now()) / 86400000);
      return days >= 0 && days <= 30;
    }).length;
    const revoked90 = keys.filter(k => {
      if (k.status !== 'REVOKED' || !k.revokedAt) return false;
      const days = Math.round((Date.now() - new Date(k.revokedAt).getTime()) / 86400000);
      return days <= 90;
    }).length;
    return { active, expiring, revoked90, total: keys.length };
  }, [keys]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Gateway · API Keys</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.active} active · {kpis.expiring} expiring within 30 days · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openCreate}>
            Create API Key
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Active keys"      value={kpis.active} tone="success" />
        <MetricCard label="Expiring ≤ 30d"   value={kpis.expiring} tone="warning" />
        <MetricCard label="Revoked / 90d"    value={kpis.revoked90} tone="muted" />
        <MetricCard label="Total"            value={kpis.total} tone="cyan" />
      </div>

      {usage && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
          <MetricCard label="API invocations"   value={Number(usage.totalInvocations || 0).toLocaleString()} tone="cyan" />
          <MetricCard label="Blocked (403 / 429)" value={Number(usage.totalBlocked || 0).toLocaleString()} tone="warning" />
          <MetricCard label="Errors"             value={Number(usage.totalErrors || 0).toLocaleString()} tone="muted" />
        </div>
      )}

      {usage && Array.isArray(usage.topKeys) && usage.topKeys.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm mb-5 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-v2-border-subtle flex items-center gap-2">
            <span className="text-[12px] font-semibold text-v2-ink">Busiest keys</span>
            <span className="text-[11px] text-v2-ink-muted">by protected-plane invocations</span>
          </div>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-v2-ink-muted">
                <th className="font-medium px-4 py-1.5">Key</th>
                <th className="font-medium px-4 py-1.5 text-right">Invocations</th>
                <th className="font-medium px-4 py-1.5 text-right">Blocked</th>
                <th className="font-medium px-4 py-1.5 text-right">Errors</th>
                <th className="font-medium px-4 py-1.5">Last used</th>
              </tr>
            </thead>
            <tbody>
              {usage.topKeys.map((k) => (
                <tr key={k.id} className="border-t border-v2-border-subtle">
                  <td className="px-4 py-1.5">
                    <span className="font-medium text-v2-ink">{k.name || '—'}</span>
                    {k.keyPrefix && <span className="ml-2 text-v2-ink-muted font-mono text-[11px]">{k.keyPrefix}…</span>}
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{Number(k.totalInvocations || 0).toLocaleString()}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{Number(k.totalBlocked || 0).toLocaleString()}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{Number(k.totalErrors || 0).toLocaleString()}</td>
                  <td className="px-4 py-1.5 text-v2-ink-secondary">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="API key filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Key size={20} />}
            title={keys.length === 0 ? 'No API keys yet' : 'No keys match filters'}
            description={keys.length === 0
              ? 'Create your first gateway API key to start authenticating agent requests.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={keys.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openCreate}>Create API Key</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="API keys"
            density={density}
            columns={[
              { key: 'name', header: 'Name', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name || r.label || '—'}</div>
                  <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{(r.id || '').slice(0, 12)}…</div>
                </div>
              )},
              { key: 'scope', header: 'Scope', render: (r) => {
                const k = scopeKind(r);
                return <Pill tone="muted" size="sm">{k === 'agent' ? 'Agent-scoped' : k === 'workflow' ? 'Workflow-scoped' : 'Tenant-wide'}</Pill>;
              }},
              { key: 'created', header: 'Created', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</span>
              )},
              { key: 'expires', header: 'Expires', render: (r) => (
                <span className={'font-mono-v2 text-[11px] ' + (r.expiresAt && (new Date(r.expiresAt).getTime() - Date.now()) < 30 * 86400000 ? 'text-v2-warning font-semibold' : 'text-v2-ink-muted')}>
                  {r.expiresAt ? fmtRelative(r.expiresAt) : 'never'}
                </span>
              )},
              { key: 'rpm', header: 'RPM', align: 'right', render: (r) => (
                <span className="tabular-nums text-[12px]">{r.rateLimitRpm ?? r.rateLimitPerMinute ?? r.rpm ?? '—'}</span>
              )},
              { key: 'status', header: 'Status', render: (r) => (
                <Pill tone={STATUS_TONE[r.status] || 'muted'} size="sm" dot>{r.status}</Pill>
              )},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {r.status === 'ACTIVE' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleRotate(r.id)} title="Rotate">
                        <RotateCcw size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleSuspend(r.id)} title="Suspend">
                        <Pause size={13} />
                      </Button>
                    </>
                  )}
                  {r.status === 'SUSPENDED' && (
                    <Button variant="ghost" size="sm" className="!text-v2-success hover:!bg-v2-success-soft"
                      onClick={() => handleReactivate(r.id)} title="Reactivate">
                      <Play size={13} />
                    </Button>
                  )}
                  {(r.status === 'ACTIVE' || r.status === 'SUSPENDED') && (
                    confirmRevokeId === r.id ? (
                      <span className="inline-flex items-center gap-1">
                        <Button variant="danger" size="sm" onClick={() => commitRevoke(r.id)}
                          title="Confirm revoke — cannot be undone">
                          <Check size={13} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmRevokeId(null)} title="Cancel">
                          <X size={13} />
                        </Button>
                      </span>
                    ) : (
                      <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                        onClick={() => armRevoke(r.id)} title="Revoke">
                        <XCircle size={13} />
                      </Button>
                    )
                  )}
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="API keys"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Key detail'}
        width={520}
        tabs={[
          { id: 'overview',       label: 'Overview' },
          { id: 'scope',          label: 'Scope' },
          { id: 'dataProtection', label: 'Data protection' },
          { id: 'audit',          label: 'Audit trail' },
          { id: 'limits',         label: 'Rate limits' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={STATUS_TONE[selected.status] || 'muted'} size="sm" dot>{selected.status}</Pill>
              <Pill tone="muted" size="sm">{(() => { const k = scopeKind(selected); return k === 'agent' ? 'Agent-scoped' : k === 'workflow' ? 'Workflow-scoped' : 'Tenant-wide'; })()}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name || selected.label}</h2>
            <p className="text-[12px] text-v2-ink-secondary font-mono-v2">{(selected.id || '').slice(0, 18)}…</p>
            <div className="flex items-center gap-2 mt-3">
              {selected.status === 'ACTIVE' && (
                <>
                  <Button variant="primary" size="sm" iconLeft={<RotateCcw size={13} />} onClick={() => handleRotate(selected.id)}>Rotate</Button>
                  <Button variant="secondary" size="sm" iconLeft={<Pause size={13} />} onClick={() => handleSuspend(selected.id)}>Suspend</Button>
                </>
              )}
              {selected.status === 'SUSPENDED' && (
                <Button variant="primary" size="sm" iconLeft={<Play size={13} />} onClick={() => handleReactivate(selected.id)}>Reactivate</Button>
              )}
              {(selected.status === 'ACTIVE' || selected.status === 'SUSPENDED') && (
                confirmRevokeId === selected.id ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[11.5px] text-v2-danger-strong font-medium">Revoke — cannot be undone?</span>
                    <Button variant="danger" size="sm" iconLeft={<XCircle size={13} />} onClick={() => commitRevoke(selected.id)}>Revoke</Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRevokeId(null)}>Cancel</Button>
                  </span>
                ) : (
                  <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<XCircle size={13} />} onClick={() => armRevoke(selected.id)}>Revoke</Button>
                )
              )}
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Overview" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Expires</dt><dd className="text-v2-ink mt-0.5">{selected.expiresAt ? new Date(selected.expiresAt).toLocaleString() : 'never'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Rate limit</dt><dd className="text-v2-ink mt-0.5">{(selected.rateLimitRpm ?? selected.rateLimitPerMinute ?? selected.rpm ?? '—')} RPM{selected.rateLimitBurst != null && <span className="text-v2-ink-muted"> · burst {selected.rateLimitBurst}</span>}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last used</dt><dd className="text-v2-ink mt-0.5">{selected.lastUsedAt ? new Date(selected.lastUsedAt).toLocaleString() : 'never'}</dd></div>
              </dl>

              {/* OAuth2 client-credentials — exchange this key for a short-lived Bearer token (Slice F3). */}
              <div className="mt-4 pt-3 border-t border-v2-line">
                <h4 className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">OAuth2 — client credentials</h4>
                <p className="text-[11.5px] text-v2-ink-muted mb-2">
                  Exchange this key for a short-lived Bearer access token. The token carries the scopes granted on the Scope tab.
                </p>
                <dl className="grid grid-cols-1 gap-2 text-[12px]">
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Token endpoint</dt>
                    <dd className="text-v2-ink mt-0.5 font-mono-v2">POST /api/v1/gateway/oauth/token</dd>
                  </div>
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">client_id</dt>
                    <dd className="text-v2-ink mt-0.5 font-mono-v2 break-all">{selected.id}</dd>
                  </div>
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">grant_type</dt>
                    <dd className="text-v2-ink mt-0.5 font-mono-v2">client_credentials</dd>
                  </div>
                </dl>
                <pre className="mt-2 p-2 rounded-v2-md border border-v2-line text-[11px] text-v2-ink overflow-x-auto whitespace-pre-wrap break-all font-mono-v2">{`curl -X POST /api/v1/gateway/oauth/token \\
  -d grant_type=client_credentials \\
  -d client_id=${selected.id} \\
  -d client_secret=<your-api-key> \\
  -d scope="workflows:read"`}</pre>
                <p className="text-[10.5px] text-v2-ink-muted mt-1">
                  client_secret is the key's secret (shown once at creation). The response returns access_token, token_type, expires_in, and the granted scope.
                </p>
              </div>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'scope' && (
          <>
          <Card padding="sm">
            <Card.Header title="Scope" />
            <Card.Body>
              {(selected.scopedAgentIds || []).length > 0 && (
                <>
                  <h4 className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Scoped agents</h4>
                  <ul className="space-y-1 text-[12px] font-mono-v2 mb-3">
                    {(selected.scopedAgentIds || []).map((id) => <li key={id} className="text-v2-ink">{id}</li>)}
                  </ul>
                </>
              )}
              {(selected.scopedWorkflowIds || []).length > 0 && (
                <>
                  <h4 className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Scoped workflows</h4>
                  <ul className="space-y-1 text-[12px] font-mono-v2 mb-3">
                    {(selected.scopedWorkflowIds || []).map((id) => <li key={id} className="text-v2-ink">{id}</li>)}
                  </ul>
                </>
              )}
              {scopeKind(selected) === 'tenant' && (
                <p className="text-[12.5px] text-v2-ink leading-relaxed">
                  This key is <strong>tenant-wide</strong> — it can authenticate requests for any agent or workflow in the tenant.
                </p>
              )}
            </Card.Body>
          </Card>
          <div className="mt-3">
            <GatewayScopePicker
              keyId={selected.id}
              value={selected.scopes || []}
              onSaved={(updated) => { if (updated) setSelected(updated); reload(); }}
            />
          </div>
          <div className="mt-3">
            <GatewayAccessTester keyId={selected.id} />
          </div>
          </>
        )}
        {selected && slideoutTab === 'dataProtection' && (
          <Card padding="sm">
            <Card.Header title="Data protection" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink-muted leading-relaxed mb-3">
                Applies <b>only to data your classification rules flag as sensitive</b> (PII, PHI, PCI, secrets, …) —
                clean content passes through untouched. Per-direction action for this key; each can only
                <b> strengthen</b> the agent's policy, never weaken it. “Inherit” uses the agent's policy. On the
                outputs (Model reply, Tool results), <b>Block</b> refuses the payload rather than returning it.
              </p>
              <a href="/policies?tab=data-classification"
                 onClick={(e) => { e.preventDefault(); navigate('/policies?tab=data-classification'); }}
                 className="mb-3 inline-block text-[12px] font-semibold text-v2-accent hover:underline">
                Manage classification rules →
              </a>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { field: 'prompt', label: 'Prompt to model' },
                  { field: 'completion', label: 'Model reply' },
                  { field: 'toolArgs', label: 'Tool inputs' },
                  { field: 'toolResult', label: 'Tool results' },
                ].map(({ field, label }) => (
                  <label key={field} className="block">
                    <span className="text-[11px] text-v2-ink-muted">{label}</span>
                    <select
                      className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                      value={dpgForm[field]}
                      onChange={(e) => setDpgForm(f => ({ ...f, [field]: e.target.value }))}
                      disabled={dpgSaving}
                    >
                      <option value="">Inherit</option>
                      <option value="LOG">Log (audit only)</option>
                      <option value="MASK">Mask</option>
                      <option value="REDACT">Redact</option>
                      <option value="TOKENIZE">Tokenize</option>
                      <option value="BLOCK">Block</option>
                    </select>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="primary" size="sm" onClick={handleSaveDpg} disabled={dpgSaving}>
                  {dpgSaving ? 'Saving…' : 'Save data protection'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            {/* G367 — Was a /gateway-keys?legacy=1 bounce. Route to
                the V2 Audit Trail page filtered to this API key id so
                operators get the same data without the V1 trip. */}
            <Card.Header title="Audit trail" actions={
              <Button variant="ghost" size="sm"
                onClick={() => navigate(`/workflow-audit?q=${encodeURIComponent(selected.id)}`)}>
                Open in Audit Trail →
              </Button>
            } />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">Recent rotations, suspensions, and revocations for this API key. Click "Open in Audit Trail" for the cryptographically-signed full trail.</p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'limits' && (
          <Card padding="sm">
            <Card.Header title="Rate limits + recent hits" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Per-minute limit</dt><dd className="text-v2-ink mt-0.5">{selected.rateLimitRpm ?? selected.rateLimitPerMinute ?? '—'} RPM</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Burst</dt><dd className="text-v2-ink mt-0.5">{selected.rateLimitBurst ?? '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Hits / 24h</dt><dd className="text-v2-ink mt-0.5">{selected.rateLimitHits24h ?? '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* Create API Key — Slideout (V2 rule: no modal overlays; use slideouts). */}
      <Slideout
        isOpen={createOpen}
        onClose={closeCreate}
        ariaLabel="Create API key"
        width={560}
        header={
          <div className="text-[15px] font-semibold text-v2-ink">
            {plaintextKey ? 'Copy your new API key — shown only once' : 'Create API Key'}
          </div>
        }
        footer={
          plaintextKey ? (
            <div className="flex items-center justify-end gap-2">
              <Button variant="primary" size="sm" onClick={() => setCreateOpen(false)}>
                Done — I've saved the key
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={closeCreate} disabled={creating}>Cancel</Button>
              <Button variant="primary" size="sm" iconLeft={<Key size={13} />}
                onClick={submitCreate} disabled={creating || !createForm.name.trim()}>
                {creating ? 'Creating…' : 'Create key'}
              </Button>
            </div>
          )
        }
      >
        {plaintextKey ? (
          <div className="space-y-3">
            <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft text-v2-warning-strong p-3 text-[12.5px] flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-0.5">Copy the plaintext key now.</div>
                <div>This is the only time the full key will be shown. The server does not store it.</div>
              </div>
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Plaintext key</span>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 bg-v2-muted border border-v2-border rounded-v2-sm px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink break-all">
                  {plaintextKey}
                </code>
                <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={copyPlaintext}>
                  {copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Copy failed' : 'Copy'}
                </Button>
              </div>
            </div>
            {plaintextKeyId && (
              <div>
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Key ID</span>
                <code className="block mt-1 text-[11.5px] font-mono-v2 text-v2-ink-secondary break-all">{plaintextKeyId}</code>
              </div>
            )}
            {/* Developer note: signing secret display for EXTERNAL keys.
              * Rendered only when the response carried plaintextHmacSecret
              * (keyType=EXTERNAL); non-EXTERNAL mints leave this null and
              * the block is skipped. Same visual pattern as the API key
              * display above so operators recognise the one-time-shown
              * treatment; separate Copy button + hmacCopyState so quick
              * copies don't fight the API key's state. */}
            {plaintextHmacSecret && (
              <div>
                <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft text-v2-warning-strong p-3 text-[12.5px] flex items-start gap-2 mb-2">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-0.5">Also copy the request-signing secret.</div>
                    <div>Your external application uses this to sign every request. Shown only once.</div>
                  </div>
                </div>
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Request-signing secret</span>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 bg-v2-muted border border-v2-border rounded-v2-sm px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink break-all">
                    {plaintextHmacSecret}
                  </code>
                  <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={copyHmacSecret}>
                    {hmacCopyState === 'copied' ? 'Copied!' : hmacCopyState === 'failed' ? 'Copy failed' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3.5">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Name *</span>
              <input
                type="text"
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                value={createForm.name}
                onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Production SDK key"
                disabled={creating}
                autoFocus
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Description</span>
              <input
                type="text"
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                disabled={creating}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Key type</span>
                <select
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={createForm.keyType}
                  onChange={(e) => setCreateForm(f => ({ ...f, keyType: e.target.value }))}
                  disabled={creating}
                >
                  {/* DEF-GATEWAY-V2-KEYTYPE-RESTORED — the V2 redesign of this
                    * page collapsed the dropdown from four canonical types
                    * down to two and conflated GATEWAY with SDK in a single
                    * "Gateway (agent SDK)" label. They're separate BE enum
                    * values with different authentication paths — see
                    * ExteletCredentialsCard.KEY_TYPE_META for the canonical
                    * taxonomy. All four are restored here. */}
                  <option value="GATEWAY">Gateway — Full proxy invocation</option>
                  <option value="SDK">SDK — @Governed decorator (split pre/post)</option>
                  <option value="MCP_GATEWAY">MCP Gateway — Off-the-shelf agents via MCP</option>
                  <option value="WORKER">Worker — Conductor SDK polling</option>
                  <option value="PROVE7_CONTAINER">Prove7 Container — Standalone agentlet container</option>
                  {/* Developer note (comment stays in source, not user-visible):
                    * EXTERNAL keyType — customer-facing external-ingestion
                    * credential. BE supports it via GatewayApiKey.KEY_TYPE_
                    * EXTERNAL; mint returns plaintextHmacSecret in the
                    * response envelope. Requires scopedWorkflowIds — see
                    * submitCreate + validator. */}
                  <option value="EXTERNAL">External — Customer applications driving workflows</option>
                </select>
                <span className="block text-[10.5px] text-v2-ink-muted mt-1 leading-snug">
                  {createForm.keyType === 'GATEWAY'   && 'Gateway proxy key — authenticates inbound traffic forwarded through the Governed Gateway.'}
                  {createForm.keyType === 'SDK'       && 'SDK key — used by the @Governed decorator in your agent code to do split pre/post governance checks.'}
                  {createForm.keyType === 'MCP_GATEWAY' && 'MCP Gateway key — authenticates MCP requests from off-the-shelf agents (Claude Desktop, etc.).'}
                  {createForm.keyType === 'WORKER'    && 'Worker key — used by Conductor-SDK workflows polling the orchestrator.'}
                  {createForm.keyType === 'PROVE7_CONTAINER' && 'Prove7 Container key — the machine credential a standalone Prove7 Multi-Agent Container uses to register, sync policy, and heartbeat over the agent-bridge. Exchanged for a short-lived token; gated on the Prove7 Container entitlement.'}
                  {createForm.keyType === 'EXTERNAL'  && 'External key — issued to customer-facing applications (mobile or web) that drive multi-step workflows on end users\' behalf. Mints an API token and a request-signing secret; both are displayed once at creation. Must be scoped to one or more specific workflows — unscoped external keys are not permitted.'}
                </span>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Expires in (days)</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={createForm.expiresInDays}
                  onChange={(e) => setCreateForm(f => ({ ...f, expiresInDays: e.target.value }))}
                  disabled={creating}
                />
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">RPM</span>
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={createForm.rateLimit}
                  onChange={(e) => setCreateForm(f => ({ ...f, rateLimit: e.target.value }))}
                  disabled={creating}
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Burst</span>
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={createForm.burstLimit}
                  onChange={(e) => setCreateForm(f => ({ ...f, burstLimit: e.target.value }))}
                  disabled={creating}
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Permissions</span>
                <input
                  type="text"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                  value={createForm.permissions}
                  onChange={(e) => setCreateForm(f => ({ ...f, permissions: e.target.value }))}
                  disabled={creating}
                  placeholder="EXECUTE,READ"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">IP allowlist (one per line, optional)</span>
              <textarea
                className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                rows={3}
                value={createForm.ipAllowlist}
                onChange={(e) => setCreateForm(f => ({ ...f, ipAllowlist: e.target.value }))}
                placeholder={'10.0.0.0/8\n192.168.1.0/24'}
                disabled={creating}
              />
            </label>
            <div className="block">
              <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Data protection (optional)</span>
              <span className="mt-1 mb-2 block text-[11px] text-v2-ink-muted">
                Applies <b>only to data your classification rules flag as sensitive</b> (PII, PHI, PCI, secrets, …) —
                clean content passes through untouched. Per-direction action for this key; each can only <b>strengthen</b>
                the agent's policy, never weaken it. Leave a direction on “Inherit” to use the agent's policy. On the
                outputs (Model reply, Tool results), <b>Block</b> refuses the payload rather than returning it.
              </span>
              <a href="/policies?tab=data-classification" target="_blank" rel="noreferrer"
                 className="mb-2 inline-block text-[11px] font-semibold text-v2-accent hover:underline">
                Manage classification rules →
              </a>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { field: 'dpgPromptAction', label: 'Prompt to model' },
                  { field: 'dpgCompletionAction', label: 'Model reply' },
                  { field: 'dpgToolArgsAction', label: 'Tool inputs' },
                  { field: 'dpgToolResultAction', label: 'Tool results' },
                ].map(({ field, label }) => (
                  <label key={field} className="block">
                    <span className="text-[11px] text-v2-ink-muted">{label}</span>
                    <select
                      className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                      value={createForm[field]}
                      onChange={(e) => setCreateForm(f => ({ ...f, [field]: e.target.value }))}
                      disabled={creating}
                    >
                      <option value="">Inherit</option>
                      <option value="LOG">Log (audit only)</option>
                      <option value="MASK">Mask</option>
                      <option value="REDACT">Redact</option>
                      <option value="TOKENIZE">Tokenize</option>
                      <option value="BLOCK">Block</option>
                    </select>
                  </label>
                ))}
              </div>
            </div>
            {/* Scope-field routing:
              *   - WORKER + EXTERNAL keys are workflow-scoped (WORKER polls
              *     an orchestrator's workflow queue; EXTERNAL authenticates a
              *     customer app driving specific workflows).
              *   - Everything else is agent-scoped (or tenant-wide).
              *   - EXTERNAL is the only one where "tenant-wide" is refused
              *     server-side, so its input surfaces that requirement inline
              *     instead of letting the operator find out via a 400. */}
            {(createForm.keyType === 'WORKER' || createForm.keyType === 'EXTERNAL') ? (
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                  {createForm.keyType === 'EXTERNAL'
                    ? 'Scoped workflow IDs (comma-/newline-separated, required)'
                    : 'Scoped workflow IDs (comma-/newline-separated, optional)'}
                </span>
                <textarea
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                  rows={2}
                  value={createForm.scopedWorkflowIds}
                  onChange={(e) => setCreateForm(f => ({ ...f, scopedWorkflowIds: e.target.value }))}
                  placeholder={createForm.keyType === 'EXTERNAL'
                    ? 'At least one workflow ID is required.'
                    : 'Leave blank for tenant-wide'}
                  disabled={creating}
                />
              </label>
            ) : (
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Scoped agent IDs (comma-/newline-separated, optional)</span>
                <textarea
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2 text-v2-ink"
                  rows={2}
                  value={createForm.scopedAgentIds}
                  onChange={(e) => setCreateForm(f => ({ ...f, scopedAgentIds: e.target.value }))}
                  placeholder="Leave blank for tenant-wide"
                  disabled={creating}
                />
              </label>
            )}
            {createError && (
              <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
                {createError}
              </div>
            )}
          </div>
        )}
      </Slideout>
    </div>
  );
}

export default function ResolvedGatewayApiKeysPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthAdministerEnabled()) return <GatewayApiKeysPage {...props} />;
  return <GatewayApiKeysPageV2Inner {...props} />;
}

export { GatewayApiKeysPageV2Inner };
